import { useRegisterParentFeedback } from '@actions';
import Button from '@components/V4/Button';
import TextArea from '@components/V4/Inputs/Textarea';
import Modal from '@components/V4/Modal/Modal';
import Pill from '@components/V4/Pill';
import StarRating from '@components/V4/StarRating/StarRating';
import { Language } from 'apps/agora/src/TranslationContext';
import useToast from 'apps/agora/src/hooks/useToast';
import { useTranslation } from 'apps/agora/src/TranslationContext';
import { useState } from 'react';
import { useParams } from 'react-router';

interface ParentPageSessionFeedbackModalProps {
  sessionId: string;
  onClose: () => void;
  language: Language;
}

const POSITIVE_RATING_THRESHOLD = 4;

const feedbackOptions = {
  good: [
    `Mentor's guidance made a positive impact`,
    `Course materials supported their learning effectively`,
    `Upgrade provided excellent support`,
  ],
  bad: [
    `Mentor's guidance could be more helpful`,
    `Course materials need improvement`,
    `Upgrade support needs to be more responsive`,
  ],
};

const feedbackOptionKeys = {
  good: [
    'feedback.suggestions.good.mentor_guidance',
    'feedback.suggestions.good.course_materials',
    'feedback.suggestions.good.upgrade_support',
  ],
  bad: [
    'feedback.suggestions.bad.mentor_guidance',
    'feedback.suggestions.bad.course_materials',
    'feedback.suggestions.bad.upgrade_support',
  ],
};

const ParentPageSessionFeedbackModal = (props: ParentPageSessionFeedbackModalProps) => {
  const { sessionId, onClose } = props;
  const { t } = useTranslation();

  const { studentId } = useParams<{ studentId: string }>();

  const token = localStorage.getItem('parentToken');
  const [rating, setRating] = useState<number>(0);
  const [selectedFeedbackSuggestions, setSelectedFeedbackSuggestions] = useState<
    Record<string, boolean>
  >({});
  const [textFeedback, setTextFeedback] = useState('');

  const toast = useToast();

  const ratingChangeHandler = (value: number) => {
    const wasPositive = rating > POSITIVE_RATING_THRESHOLD;
    const isPositive = value > POSITIVE_RATING_THRESHOLD;

    if (wasPositive !== isPositive) {
      setSelectedFeedbackSuggestions({});
    }

    setRating(value);
  };

  const ratingQuality = rating > 4 ? 'good' : 'bad';

  const { mutate: registerParentFeedback, isLoading } = useRegisterParentFeedback(sessionId, {
    onSuccess: () => {
      onClose();
    },
    onError: () => toast.error(t('feedback.error_message')),
  });

  const submitHandler = () => {
    const selectedSuggestions = Object.keys(selectedFeedbackSuggestions)
      .filter((key) => !!selectedFeedbackSuggestions[key])
      .map((key) => feedbackOptions[ratingQuality][feedbackOptionKeys[ratingQuality].indexOf(key)])
      .join(', ');

    const message = (selectedSuggestions ? selectedSuggestions + '. \n' : '') + textFeedback;

    registerParentFeedback({
      rating: rating,
      message: message,
      token: token ?? '',
      userId: studentId,
    });
  };

  return (
    <Modal isOpen={true} onClose={onClose}>
      <Modal.Header title={t('feedback.title')}></Modal.Header>

      <Modal.Body>
        <StarRating
          className="mt-12 w-full items-center"
          value={rating}
          onChange={ratingChangeHandler}
        />
        <div className="flex flex-col gap-6 mt-12">
          {!!rating && (
            <>
              <p>
                {ratingQuality === 'good'
                  ? t('feedback.positive_message')
                  : t('feedback.negative_message')}
              </p>
              <div className="flex flex-wrap gap-4">
                {feedbackOptionKeys[ratingQuality].map((feedbackKey) => (
                  <Pill
                    key={feedbackKey}
                    size="default"
                    color="light"
                    text={t(feedbackKey)}
                    isSelected={selectedFeedbackSuggestions[feedbackKey]}
                    onClick={() =>
                      setSelectedFeedbackSuggestions((prev) => ({
                        ...prev,
                        [feedbackKey]: !prev[feedbackKey],
                      }))
                    }
                  />
                ))}
              </div>
              <TextArea
                value={textFeedback}
                onChange={(e) => setTextFeedback(e.target.value)}
                className="mb-6"
                name="other-feedback"
                id="other-feedback"
                label={t('feedback.other_label')}
              />

              <Button
                size="large"
                className="w-40 self-end"
                buttonText={t('feedback.submit_button')}
                isLoading={isLoading}
                onClick={submitHandler}
                disabled={rating === 0}
              />
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ParentPageSessionFeedbackModal;
